import {
  evidenceAdapterDescriptorSchema,
  type EvidenceAdapter,
  type EvidenceAdapterDescriptor,
  type ExecutionTarget,
} from './types.js';

export class EvidenceAdapterRegistry {
  readonly #adapters = new Map<string, EvidenceAdapter>();
  readonly #descriptors = new Map<string, EvidenceAdapterDescriptor>();

  register(adapter: EvidenceAdapter): void {
    const descriptor = evidenceAdapterDescriptorSchema.parse(adapter.descriptor);
    if (this.#descriptors.has(descriptor.id)) throw new Error(`Adapter already registered: ${descriptor.id}`);
    this.#adapters.set(descriptor.id, adapter);
    this.#descriptors.set(descriptor.id, Object.freeze(descriptor));
  }

  registerDescriptor(input: EvidenceAdapterDescriptor): void {
    const descriptor = evidenceAdapterDescriptorSchema.parse(input);
    if (descriptor.availability === 'AVAILABLE') {
      throw new Error(`Descriptor ${descriptor.id} cannot be AVAILABLE without an executable adapter`);
    }
    if (this.#descriptors.has(descriptor.id)) throw new Error(`Adapter already registered: ${descriptor.id}`);
    this.#descriptors.set(descriptor.id, Object.freeze(descriptor));
  }

  descriptors(): readonly EvidenceAdapterDescriptor[] {
    return [...this.#descriptors.values()].sort((left, right) => left.id.localeCompare(right.id));
  }

  executable(runtime: ExecutionTarget): readonly EvidenceAdapter[] {
    return [...this.#adapters.values()]
      .filter(({ descriptor }) => descriptor.availability === 'AVAILABLE' && descriptor.execution.includes(runtime))
      .sort((left, right) => left.descriptor.id.localeCompare(right.descriptor.id));
  }

  supports(mediaType: string, runtime: ExecutionTarget): boolean {
    return this.executable(runtime).some(({ descriptor }) => descriptor.media_types.includes(mediaType));
  }
}

export interface ExternalEvidencePolicyConfiguration {
  allowedNetworkAdapterIds?: readonly string[];
}

export class ExternalEvidencePolicy {
  readonly #allowed: ReadonlySet<string>;

  constructor(configuration: ExternalEvidencePolicyConfiguration = {}) {
    this.#allowed = new Set(configuration.allowedNetworkAdapterIds ?? []);
  }

  allows(descriptor: EvidenceAdapterDescriptor): boolean {
    if (!descriptor.network.requiresNetwork) return descriptor.network.mediaTransmission === 'NONE';
    return this.#allowed.has(descriptor.id);
  }
}
